import {sleep} from '@benchling/js-utils';
import {captureException} from '@sentry/browser';

import {assert} from 'Asserts';
import AutoSaver from 'notebook/store/auto-save/AutoSaver';
import bufferForUX from 'util/bufferForUX';
import {withLoadingScreen} from 'util/Promises';

class ReadOnlyProcedureError extends Error {}

const NO_OVERLAY = '_NO_OVERLAY_';

const autosaveManagersByItemId: Record<string, AutosaveManager<unknown, unknown, unknown>> = {};

type SaveHandler<TData, TResponse, TSaveContext> = (
  data: TData,
  isBlocking: boolean,
  saveContext?: TSaveContext
) => Promise<TResponse>;

interface Callbacks<TData, TResponse, TSaveContext> {
  seed: (itemId: string) => void;
  isDirty: (itemId: string) => boolean;
  isReadOnly: (itemId: string) => boolean;
  shouldSaveNow: (itemId: string) => boolean;
  getDataToSave: (itemId: string) => TData;
  setIsSaving: (itemId: string, isSaveInFlight: boolean) => void;
  doSave: SaveHandler<TData, TResponse, TSaveContext>;
}

/**
 * The AutosaveManager is a custom wrapper around the AutoSaver that provides a number of helpful features:
 *
 * 1. It's truly generic. You can see from the template types that it makes no assumptions about the data being saved.
 *    It doesn't own (or even know about) any data, nor any of the control values. These are all provided by callbacks.
 *
 * 2. It's fully and easily controlled. The callbacks allow users to fine-tune its behavior in any way they see fit.
 *
 * 3. It's offers not only the basic automatic save, but also an option to save immediately. It manages a small
 *    queue of saves (max size = 1 lol) to prevent overlapping API calls. The queue could be easily and transparently
 *    upgraded.
 *
 * 4. It offers an extremely lean API, so callers don't really need to know much about the internals.
 */
class AutosaveManager<TData, TResponse, TSaveContext> {
  private readonly itemId: string;
  /** User-facing name for the object being saved, used for autosaver notifications. */
  private readonly friendlyName: string;
  private autosaver: AutoSaver | null;
  private readonly callbacks: Callbacks<TData, TResponse, TSaveContext>;
  private pendingSave: Promise<TResponse> | null;

  constructor(itemId: string, friendlyName: string, callbacks: Callbacks<TData, TResponse, TSaveContext>) {
    this.itemId = itemId;
    this.friendlyName = friendlyName;
    assert(autosaveManagersByItemId[itemId] == null, 'Autosaver has already been configured', {itemId});
    autosaveManagersByItemId[itemId] = this;
    this.autosaver = null;
    this.callbacks = callbacks;
    this.pendingSave = null;

    this.enableAutosaver();
  }

  private getItemStateForAutosaver(): {openVersion: number; savedVersion: number} {
    return {
      openVersion: 1,
      savedVersion: this.callbacks.isDirty(this.itemId) ? 0 : 1,
    };
  }

  private enableAutosaver(): void {
    assert(this.autosaver == null, 'Autosaver is already enabled', {itemId: this.itemId});

    // Subtle naming alert: we create an `AutoSaver` from within the `AutosaveManager`.
    this.autosaver = new AutoSaver({
      id: this.itemId,
      friendlyName: this.friendlyName,
      doSave: () => this.save(),
      getItemState: () => this.getItemStateForAutosaver(),
      persistToLocalStorage: false,
      checkItemLoaded: false,
      showErrors: false,
      suppressNotifications: true,
    });
  }
  cleanup(): void {
    if (this.autosaver) {
      this.autosaver.stop();
      this.autosaver = null;
    }
    delete autosaveManagersByItemId[this.itemId];
  }

  updateSaveHandler(itemId: string, doSave: SaveHandler<TData, TResponse, TSaveContext>): void {
    assert(this.itemId === itemId, 'Cannot update save handler for a different item', {
      itemId,
      thisItemId: this.itemId,
    });
    this.callbacks.doSave = doSave;
  }

  /**
   * This is the save handler we install into the autosaver.
   */
  private save(): JQueryPromise<boolean> {
    if (!this.callbacks.shouldSaveNow(this.itemId) || this.pendingSave != null) {
      return $.Deferred<boolean>().resolve(false).promise();
    }

    // Create a deferred promise that will be resolved once the save has completed.
    const deferredSavePromise = $.Deferred<boolean>();
    this.handleSave(false)
      .then(() => {
        deferredSavePromise.resolve(true);
      })
      .catch((e) => {
        // For errors that are transient (eg. the server is unreachable), we resolve the promise with false
        // indicating that the save failed. Otherwise, we reject the promise with the error.
        //
        // On transient errors, the autosaver will continue retrying, but rejecting the promise with the error
        // will cause the autosaver to stop retrying and error will bubble up.
        if (e instanceof ReadOnlyProcedureError || e?.status === 0) {
          deferredSavePromise.resolve(false);
        } else {
          deferredSavePromise.reject(e);
        }
      });
    return deferredSavePromise;
  }

  private async handleSave(isBlocking: boolean, saveContext?: TSaveContext): Promise<TResponse> {
    if (this.callbacks.isReadOnly(this.itemId)) {
      /**
       * Read-only procedures can actually reach this function! This can happen if the document makes
       * some kind of change when the document opens. E.g., automatically collapsing a table.
       */
      throw new ReadOnlyProcedureError('Save is disabled in a read-only procedure');
    }

    // This function might be invoked while an existing save is in flight. We can't begin
    // a new save until any existing save operation has returned.
    while (this.pendingSave != null) {
      await this.pendingSave;
    }

    const prepareSavePromise = async (): Promise<TResponse> => {
      this.callbacks.setIsSaving(this.itemId, true);

      // We need to defer invoking the API for a tick. This ensures that any state
      // updates can flush, and the save will act on the most recent data we have. This is needed
      // whether we had to wait for a prior autosave to complete or not.
      await sleep(0);

      try {
        const dataToSave = this.callbacks.getDataToSave(this.itemId);
        const data = await this.callbacks.doSave(dataToSave, isBlocking, saveContext);

        // Finally: we add one more brief pause to allow the "bookkept" flowchart to flush. Anyone calling
        // `handleSave` can expect that all the server data has been properly merged into the flowchart, as well
        // as any render side-effects (such as useEffects and ref updating) to complete. We will only set
        // pendingSave = null after this point, ensuring that no other save enters the critical section
        // before the render cycle completes.
        await sleep(0);

        return data;
      } finally {
        // Guarantees that pendingSave is null after you await it.
        this.pendingSave = null;
        this.callbacks.setIsSaving(this.itemId, false);
      }
    };

    this.pendingSave = prepareSavePromise();
    return await this.pendingSave;
  }

  async handleSaveImmediately(
    actAfterInflightSaves: () => TSaveContext,
    overlayMessage?: string,
    shouldBubbleUpErrors: boolean = true
  ): Promise<TResponse> {
    const bufferLogic = async (): Promise<TResponse> => {
      // Queue up behind any running call to `handleSave`
      while (this.pendingSave != null) {
        await this.pendingSave;
      }

      const saveContext = actAfterInflightSaves();
      // actAfterInflightSaves is free to modify the saveData in Redux, so we wait a tick for
      // the Redux actions to flush
      await sleep(0);
      return await this.handleSave(true, saveContext);
    };

    // @ts-expect-error This will get handled by standard client error handling if an error is thrown
    const bufferedPromise = async (withUxBuffer: boolean): Promise<TResponse> => {
      try {
        // bufferForUX is handling errors in a strange way, which causes duplicate error toasts
        // if we don't catch and swallow it here
        return await bufferForUX(Promise.resolve(bufferLogic()), withUxBuffer ? 600 : 0);
      } catch (e) {
        // This function will swallow the duplicate error toasts (it will still be raised elsewhere!)
        // Unless the caller explicitly requests that the error be rethrown so they can handle it themselves
        // In those cases, the caller should swallow the error and take whatever action is desired
        if (shouldBubbleUpErrors) {
          throw e;
        }
        // Safety net to ensure that we have a log of any errors that are swallowed without raising to users
        captureException(e.statusText, {extra: {error: e.responseJson}});
      }
    };

    if (overlayMessage === NO_OVERLAY) {
      return await bufferedPromise(false);
    } else {
      return await withLoadingScreen(bufferedPromise(true), overlayMessage ?? 'Saving...');
    }
  }
}

async function initializeAutosaveManager<TData, TResponse, TSaveContext>(
  itemId: string,
  friendlyName: string,
  callbacks: Callbacks<TData, TResponse, TSaveContext>
): Promise<void> {
  // We block creation of a duplicate autosaver for the same itemId until the existing one is destroyed.
  // This ensures that the user can't break the AutosaveManager by navigating away, then quickly returning
  // before the save has completed. This is safe because the only management of the relevant Autosaver is
  // done here and in `destroy` below.

  /**
   * NOTE: if you need alternate behavior than described above, you need to handle it yourself outside the ASM.
   * E.g., if your application allows for multiple autosaver-enabled instances of the same object with the same ID,
   * this code won't work for you as is. A simple solution would be to use a different ID for each instance.
   */
  while (exists(itemId)) {
    await sleep(100);
  }
  // This ensures that the Redux data is initialized before the autosaver starts
  callbacks.seed(itemId);

  // oxlint-disable-next-line no-new
  new AutosaveManager(itemId, friendlyName, callbacks);
}

function destroyAutosaveManager(itemId: string): void {
  const autosaveManager = autosaveManagersByItemId[itemId];
  autosaveManager?.cleanup();
}

function updateSaveHandler<TData, TResponse, TSaveContext>(
  itemId: string,
  doSave: SaveHandler<TData, TResponse, TSaveContext>
): void {
  const autosaveManager = autosaveManagersByItemId[itemId];
  autosaveManager?.updateSaveHandler(itemId, doSave);
}

function saveImmediately<TData, TResponse, TSaveContext>(
  itemId: string,
  actAfterInflightSaves: () => TSaveContext,
  overlayMessage?: string,
  shouldBubbleUpErrors: boolean = true
): Promise<TResponse> {
  const autosaveManager = autosaveManagersByItemId[itemId] as AutosaveManager<TData, TResponse, TSaveContext>;
  return autosaveManager?.handleSaveImmediately(actAfterInflightSaves, overlayMessage, shouldBubbleUpErrors);
}

export function cleanupForTests(): void {
  for (const key of Object.keys(autosaveManagersByItemId)) {
    delete autosaveManagersByItemId[key];
  }
}

function exists(itemId: string): boolean {
  return autosaveManagersByItemId[itemId] != null;
}

export default {
  initialize: initializeAutosaveManager,
  destroy: destroyAutosaveManager,
  exists,
  updateSaveHandler,
  saveImmediately,

  // Pass this constant to `saveImmediately` to create a non-blocking but immediate save
  NO_OVERLAY,
};
